/**
 * One back-navigation treatment for the whole portal. Pages previously
 * hand-rolled "← Back" / "← All capabilities" links with their own markup;
 * this is the single component they all use.
 *
 * DESIGN-SYSTEM CANDIDATE — see docs/DESIGN-SYSTEM-CANDIDATES.md.
 */
import { Link } from 'react-router-dom';
import { ArrowLeft } from '@servicecompressionllc/sc-design-system/icons';

/** A crumb is a route, or a handler when the destination is "wherever you
 *  came from" (IntegrationFormPage pops history deliberately). */
export type Crumb = { label: string } & ({ to: string } | { onClick: () => void });

/** Matches the portal's established back-link treatment (Groups, Users):
 *  ArrowLeft + muted label, not a blue link. */
const CRUMB_LINK = 'text-ink-muted hover:text-ink';

export const Breadcrumbs = ({ trail }: { trail: Crumb[] }) => (
  <nav aria-label="Breadcrumb" className="mb-4">
    <ol className="flex flex-wrap items-center gap-1 text-sm">
      {trail.map((c, idx) => (
        <li key={c.label} className="inline-flex items-center gap-1">
          {idx === 0 && <ArrowLeft size={16} className="text-ink-muted" aria-hidden />}
          {'to' in c ? (
            <Link to={c.to} className={CRUMB_LINK}>
              {c.label}
            </Link>
          ) : (
            <button type="button" onClick={c.onClick} className={CRUMB_LINK}>
              {c.label}
            </button>
          )}
          {idx < trail.length - 1 && <span className="text-ink-subtle">/</span>}
        </li>
      ))}
    </ol>
  </nav>
);
